import React, { useState, useContext } from 'react';
import { Grid, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../../context/Context';

function Shortcuts({ updateAmountShortcut, isTab }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const { userLimits } = useContext(AppContext);

  const allValues = [10, 20, 30, 40, 50, 60, 100, 150, 200, 300, 500, 1000];

  const maxAllowed = (() => {
    if (!userLimits?.daily || !userLimits?.monthly) return Infinity;

    const dailyRaw =
      userLimits.daily.limit - (userLimits.daily.cashIn - userLimits.daily.cashOut);
    const monthlyRaw =
      userLimits.monthly.limit -
      (userLimits.monthly.cashIn - userLimits.monthly.cashOut + userLimits.monthly.rollover);

    const dailyAvailable = dailyRaw < 0 ? Infinity : dailyRaw;
    const monthlyAvailable = monthlyRaw < 0 ? Infinity : monthlyRaw;

    return Math.min(dailyAvailable, monthlyAvailable);
  })();

  // Always 12 buttons: first 11 fixed, last slot is "Max" when capped below 1000
  const displayValues = allValues.slice(0, 11).map((v) => ({
    label: String(v),
    value: v,
    disabled: v > maxAllowed,
  }));
  if (maxAllowed < 1000) {
    displayValues.push({ label: 'Max', value: maxAllowed, disabled: false });
  } else {
    displayValues.push({ label: '1000', value: 1000, disabled: false });
  }

  const getBtnSx = (label, isDisabled) => {
    const isSelected = !isDisabled && selected === label;
    return {
      height: '100%',
      width: '100%',
      fontSize: isTab ? '34px' : '44px',
      fontWeight: 900,
      borderRadius: '16px',
      border: isSelected
        ? '1.5px solid rgba(255,199,39,0.85)'
        : isDisabled
        ? '1px solid rgba(255,255,255,0.06)'
        : '1px solid rgba(255,255,255,0.14)',
      background: isSelected
        ? 'linear-gradient(180deg, rgba(255,199,39,0.28), rgba(255,199,39,0.14))'
        : isDisabled
        ? 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))'
        : 'linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))',
      boxShadow: isDisabled
        ? 'none'
        : isSelected
        ? '0 0 18px rgba(255,199,39,0.35), 0 10px 22px rgba(0,0,0,0.35)'
        : '0 10px 22px rgba(0,0,0,0.35)',
      color: isDisabled ? 'rgba(255,255,255,0.2)' : isSelected ? '#FFC727' : 'white',
      textTransform: 'none',
      pointerEvents: isDisabled ? 'none' : 'auto',
      transition: 'all 0.15s ease',
      '&:hover': isDisabled
        ? {}
        : {
            transform: 'translateY(-2px)',
            boxShadow: isSelected
              ? '0 0 24px rgba(255,199,39,0.5), 0 14px 28px rgba(0,0,0,0.45)'
              : '0 14px 28px rgba(0,0,0,0.45)',
            background: isSelected
              ? 'linear-gradient(180deg, rgba(255,199,39,0.36), rgba(255,199,39,0.20))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.08))',
          },
      '&:active': isDisabled ? {} : { transform: 'translateY(0px)' },
    };
  };

  return (
    <Grid
      item
      xs={12}
      sx={{
        padding: isTab ? '0px' : '10px',
        height: '100%',
        minHeight: isTab ? 0 : '555px',
        width: '100%',
        marginTop: 0,
        marginLeft: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
      aria-label={t('sales.shortcuts')}
    >
      <Grid
        container
        flexDirection="row"
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: '100%',
          marginTop: 0,
          marginLeft: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignContent: 'space-evenly',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {displayValues.map(({ label, value, disabled }) => (
          <Grid
            key={label}
            item
            xs={4}
            sx={{
              display: 'flex',
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Grid
              item
              sx={{
                height: isTab ? '130px' : '22vh',
                width: isTab ? '130px' : '22vh',
                maxWidth: isTab ? '130px' : '180px',
                maxHeight: isTab ? '130px' : '180px',
                display: 'flex',
                alignContent: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="contained"
                component="label"
                sx={getBtnSx(label, disabled)}
                onClick={() => {
                  if (disabled) return;
                  setSelected(label);
                  updateAmountShortcut(value);
                }}
              >
                {label}
              </Button>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}

export default Shortcuts;
